// Clean one-pager IC document — captured by html2canvas to share with BD team.
// Composes header + property snapshot + capacity strip + BD estimates from
// inline blocks, and delegates the P&L (largest block) to `ICDocPnlSection.jsx`.
// Shared primitives (ColorStripe / MetaRow / SectionTitle) live in `_ic_doc_primitives.jsx`.

import { fmtINR, fmtL, fmtPct, fmtNum, decisionTone } from "./utils";
import { ColorStripe, MetaRow, SectionTitle } from "./_ic_doc_primitives";
import ICDocPnlSection from "./ICDocPnlSection";

export default function ICDocument({
  meta,
  params,
  rooms,
  estDorm,
  estPriv,
  calc,
  breakeven,
  user,
  generatedAt,
  decision,
}) {
  const sc = calc?.base_case || {};
  const rs = calc?.room_summary || {};
  const decisionLabel = decision || sc.decision_recommendation || "—";
  const dateLabel = generatedAt ? new Date(generatedAt).toLocaleString("en-IN", {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  }) : "";

  return (
    <div className="bg-white" style={{ width: 820, fontFamily: "'Figtree', system-ui, sans-serif", color: "#09090B" }}>
      <ColorStripe />

      {/* Header */}
      <header className="px-8 pt-6 pb-4 flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">
            goSTOPS · Investment Committee
          </div>
          <h1 className="font-display font-black tracking-tight text-zinc-900"
              style={{ fontSize: 28, lineHeight: 1.2, paddingBottom: 2, marginTop: 4 }}>
            {meta?.property_name || "Untitled Property"}
          </h1>
          <div className="text-[12px] text-zinc-600 mt-1">
            {[meta?.city, meta?.state].filter(Boolean).join(", ") || "Location TBD"}
            {meta?.partner_name ? ` · ${meta.partner_name}` : ""}
          </div>
        </div>
        <div className={`shrink-0 px-3 py-2 rounded-md border-2 text-center ${decisionTone(decisionLabel)}`}
             style={{ minWidth: 130 }}>
          <div className="text-[9px] font-bold uppercase tracking-wider opacity-80">Decision</div>
          <div className="font-display font-black tracking-tight"
               style={{ fontSize: 15, lineHeight: 1.3, paddingBottom: 2, marginTop: 2 }}>
            {decisionLabel}
          </div>
          {sc.roi_months ? (
            <div className="text-[10.5px] font-bold mt-0.5 tabular-nums">{sc.roi_months} mo ROI</div>
          ) : null}
        </div>
      </header>

      {/* Property snapshot */}
      <section className="px-8">
        <SectionTitle accent="#FFCC00">Property snapshot</SectionTitle>
        <div className="grid grid-cols-4 gap-x-5 gap-y-3 bg-zinc-50/80 border border-zinc-200 rounded-lg p-3">
          <MetaRow label="Type" value={params?.type} />
          <MetaRow label="Vintage" value={params?.vintage} />
          <MetaRow label="Model" value={params?.model} />
          <MetaRow label="Op start" value={meta?.operation_start_date} />
          <MetaRow label="Capex" value={fmtL(sc.capex)} />
          <MetaRow label="Capex / bed" value={sc.capex_per_bed ? fmtINR(sc.capex_per_bed) : "—"} />
          <MetaRow label="F&B / bed-day" value={params?.fnb_per_bed_per_day ? `₹${params.fnb_per_bed_per_day}` : "—"} />
          <MetaRow label="Property link" value={meta?.property_link ? "Attached" : "—"} />
        </div>
      </section>

      <CapacityStrip rs={rs} sc={sc} />

      <BdEstimatesStrip estDorm={estDorm} estPriv={estPriv} />

      <ICDocPnlSection sc={sc} rs={rs} breakeven={breakeven} decision={decisionLabel} model={params?.model} />

      {/* Notes */}
      {meta?.notes ? (
        <section className="px-8 mt-3">
          <SectionTitle accent="#FFA500">Notes</SectionTitle>
          <div className="rounded-md border border-zinc-200 bg-zinc-50/60 p-3 text-[11.5px] text-zinc-700 leading-snug whitespace-pre-line">
            {meta.notes}
          </div>
        </section>
      ) : null}

      {/* Footer */}
      <footer className="mt-5">
        <div className="px-8 pb-3 flex items-center justify-between gap-3 text-[10px] text-zinc-500">
          <div>
            Prepared by <span className="font-bold text-zinc-700">{user?.name || user?.email || "BD team"}</span>
            {dateLabel ? ` · ${dateLabel}` : ""}
          </div>
          <div className="font-bold tracking-wider uppercase">goSTOPS · BD Command Center</div>
        </div>
        <ColorStripe />
      </footer>
    </div>
  );
}

// Capacity strip — 5 small KPI cards (total/dorm/private beds + rooms + bed-nights).
function CapacityStrip({ rs, sc }) {
  const cards = [
    { label: "Total beds",     value: fmtNum(rs.total_beds),    accent: "#FFCC00" },
    { label: "Total rooms",    value: fmtNum(rs.total_rooms),   accent: "#FF69B4" },
    { label: "Dorm beds",      value: fmtNum(rs.dorm_beds),     accent: "#20B2AA" },
    { label: "Private beds",   value: fmtNum(rs.private_beds),  accent: "#9370DB" },
    { label: "Bed-nights / mo",value: fmtNum(sc.total_bed_nights), accent: "#FFA500" },
    ...(rs.blocked_beds > 0 ? [{ label: "Blocked beds", value: fmtNum(rs.blocked_beds), accent: "#71717A" }] : []),
  ];
  return (
    <section className="px-8">
      <SectionTitle accent="#FF69B4">Capacity</SectionTitle>
      <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${cards.length}, minmax(0, 1fr))` }}>
        {cards.map((k) => (
          <div key={k.label} className="rounded-md border border-zinc-200 bg-white p-3">
            <div className="text-[9px] font-bold uppercase tracking-wider text-zinc-500">{k.label}</div>
            <div className="font-display font-black text-[18px] tabular-nums mt-1"
                 style={{ lineHeight: 1.6, paddingBottom: 4, minHeight: 30 }}>
              {k.value}
            </div>
            <div className="mt-1 h-[2px] w-6 rounded-full" style={{ background: k.accent }} />
          </div>
        ))}
      </div>
    </section>
  );
}

// BD estimates strip — Dorm + Private occupancy / ADR (6-member averages).
function BdEstimatesStrip({ estDorm, estPriv }) {
  return (
    <section className="px-8">
      <SectionTitle accent="#20B2AA">BD estimates · 6 members averaged</SectionTitle>
      <div className="grid grid-cols-2 gap-3">
        <BdEstimateCard label="Dorm" est={estDorm} adrSuffix="bed" />
        <BdEstimateCard label="Private" est={estPriv} adrSuffix="room" />
      </div>
    </section>
  );
}

function BdEstimateCard({ label, est, adrSuffix }) {
  return (
    <div className="rounded-md border border-zinc-200 bg-white p-3">
      <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-2">{label}</div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <div className="text-[9px] uppercase tracking-wide text-zinc-500">Occupancy</div>
          <div className="font-display font-black text-[18px] tabular-nums"
               style={{ lineHeight: 1.6, paddingBottom: 4, minHeight: 30 }}>{fmtPct(est?.occ_pct)}</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-wide text-zinc-500">ADR / {adrSuffix}</div>
          <div className="font-display font-black text-[18px] tabular-nums"
               style={{ lineHeight: 1.6, paddingBottom: 4, minHeight: 30 }}>{fmtINR(est?.adr)}</div>
        </div>
      </div>
    </div>
  );
}
